import { boardService } from './board.service';
import { supabaseWorkspaceService } from './supabase-workspace.service';
// Utility function to generate unique IDs
function generateId() {
    return Math.random().toString(36).substr(2, 9);
}
export class WorkspaceService {
    workspaces = new Map();
    workspaceMembers = new Map();
    constructor() {
        // Initialize with Supabase data
        this.loadWorkspacesFromSupabase();
    }
    /**
     * Clear the workspace cache to force a fresh load from Supabase
     */
    clearCache() {
        console.log('🧹 Clearing workspace cache');
        this.workspaces.clear();
        this.workspaceMembers.clear();
    }
    /**
     * Load workspaces from Supabase, optionally filtered by organization
     */
    async loadWorkspacesFromSupabase(organizationId) {
        try {
            console.log('🔄 Loading workspaces from Supabase...', organizationId ? `for org ${organizationId}` : '');
            const supabaseWorkspaces = await supabaseWorkspaceService.loadWorkspaces(organizationId);
            // Clear existing workspaces
            this.workspaces.clear();
            // Add Supabase workspaces
            supabaseWorkspaces.forEach(workspace => {
                this.workspaces.set(workspace.id, workspace);
                console.log(`✅ Loaded workspace: ${workspace.name}`);
            });
            // If no workspaces loaded, create default
            if (supabaseWorkspaces.length === 0) {
                console.log('⚠️ No workspaces found in Supabase, creating default...');
                this.createDefaultWorkspace();
            }
        }
        catch (error) {
            console.error('❌ Failed to load workspaces from Supabase:', error);
            this.createDefaultWorkspace();
        }
    }
    /**
     * Sync workspace boards with COW board system
     */
    async syncWithCOWBoards() {
        try {
            // Get all COW boards
            const cowBoards = await boardService.getBoards();
            // Update workspace boards to match COW boards
            for (const workspace of this.workspaces.values()) {
                const workspaceBoards = cowBoards.map(cowBoard => ({
                    id: cowBoard.id,
                    name: cowBoard.title,
                    description: cowBoard.description,
                    workspaceId: workspace.id,
                    folderId: undefined, // Will be set based on existing workspace structure
                    ownerId: cowBoard.ownerId || 'current-user',
                    color: '#579bfc', // Default color, can be customized
                    starred: cowBoard.isStarred || false,
                    boardType: 'flexiboard',
                    createdAt: cowBoard.createdAt,
                    updatedAt: cowBoard.updatedAt
                }));
                // Update workspace boards
                workspace.boards = workspaceBoards;
                workspace.updatedAt = new Date();
            }
        }
        catch (error) {
            console.warn('Failed to sync with COW boards:', error);
        }
    }
    createDefaultWorkspace() {
        const defaultWorkspace = {
            id: 'workspace-main',
            name: 'Main Workspace',
            description: 'Your default workspace for company-wide collaboration',
            type: 'open',
            color: '#579bfc',
            icon: '🏢',
            isDefault: true,
            ownerId: 'current-user',
            memberIds: ['current-user'],
            createdAt: new Date(),
            updatedAt: new Date(),
            folders: [],
            boards: [],
            dashboards: [],
            docs: [],
            forms: [],
            apps: []
        };
        this.workspaces.set(defaultWorkspace.id, defaultWorkspace);
        // Add sample folders and boards
        this.createSampleContent(defaultWorkspace.id);
    }
    createSampleContent(workspaceId) {
        const workspace = this.workspaces.get(workspaceId);
        if (!workspace)
            return;
        // Create Marketing folder
        const marketingFolder = {
            id: 'folder-marketing',
            name: 'Marketing',
            color: '#ff9f40',
            workspaceId,
            collapsed: false,
            boards: [],
            dashboards: [],
            docs: [],
            forms: [],
            apps: [],
            subFolders: [],
            createdAt: new Date(),
            updatedAt: new Date()
        };
        // Create HR folder
        const hrFolder = {
            id: 'folder-hr',
            name: 'Human Resources',
            color: '#00c875',
            workspaceId,
            collapsed: false,
            boards: [],
            dashboards: [],
            docs: [],
            forms: [],
            apps: [],
            subFolders: [],
            createdAt: new Date(),
            updatedAt: new Date()
        };
        workspace.folders = [marketingFolder, hrFolder];
        // Note: Sample board creation disabled due to RLS policy recursion issue
        // TODO: Fix RLS policy infinite recursion in board_members relation
        // this.createSampleBoardsAsync(workspaceId, marketingFolder.id, hrFolder.id);
    }
    /**
     * Get all workspaces for current user
     */
    getAllWorkspaces() {
        return Array.from(this.workspaces.values());
    }
    /**
     * Get workspace by ID
     */
    getWorkspaceById(id) {
        return this.workspaces.get(id);
    }
    /**
     * Create a new workspace
     */
    createWorkspace(data) {
        const workspace = {
            id: generateId(),
            ...data,
            ownerId: 'current-user',
            memberIds: ['current-user'],
            isDefault: false,
            createdAt: new Date(),
            updatedAt: new Date(),
            folders: [],
            boards: [],
            dashboards: [],
            docs: [],
            forms: [],
            apps: []
        };
        this.workspaces.set(workspace.id, workspace);
        // Create initial folders for the new workspace
        this.createInitialFolders(workspace.id);
        return workspace;
    }
    /**
     * Create initial folders for a new workspace
     */
    createInitialFolders(workspaceId) {
        const workspace = this.workspaces.get(workspaceId);
        if (!workspace)
            return;
        // Create a general folder to get users started
        const generalFolder = {
            id: `folder-${generateId()}`,
            name: 'General',
            color: '#579bfc',
            workspaceId,
            collapsed: false,
            boards: [],
            dashboards: [],
            docs: [],
            forms: [],
            apps: [],
            subFolders: [],
            createdAt: new Date(),
            updatedAt: new Date()
        };
        workspace.folders = [generalFolder];
        workspace.updatedAt = new Date();
    }
    /**
     * Update workspace
     */
    updateWorkspace(id, updates) {
        const workspace = this.workspaces.get(id);
        if (!workspace)
            return null;
        const updated = { ...workspace, ...updates, updatedAt: new Date() };
        this.workspaces.set(id, updated);
        return updated;
    }
    /**
     * Delete workspace
     */
    deleteWorkspace(id) {
        const workspace = this.workspaces.get(id);
        if (!workspace || workspace.isDefault)
            return false;
        return this.workspaces.delete(id);
    }
    /**
     * Create folder in workspace or from modal data
     */
    async createFolder(workspaceIdOrData, data) {
        // Handle both old API (workspaceId, data) and new API (data)
        let workspaceId;
        let folderData;
        if (typeof workspaceIdOrData === 'string') {
            workspaceId = workspaceIdOrData;
            folderData = data;
        }
        else {
            folderData = workspaceIdOrData;
            workspaceId = folderData.workspaceId;
        }
        const workspace = this.workspaces.get(workspaceId);
        if (!workspace)
            return null;
        try {
            console.log('📂 workspace.service.ts: Creating folder via Supabase...', folderData);
            // Create folder in Supabase
            const supabaseFolder = await supabaseWorkspaceService.createFolder({
                workspaceId,
                name: folderData.name,
                description: folderData.description || '',
                color: folderData.color,
                parentId: folderData.parentId,
                createdBy: folderData.ownerId
            });
            if (!supabaseFolder) {
                console.error('❌ Failed to create folder in Supabase');
                return null;
            }
            // Convert Supabase folder to workspace folder format
            const folder = {
                id: supabaseFolder.id,
                name: supabaseFolder.name,
                description: supabaseFolder.description || '',
                color: supabaseFolder.color,
                parentId: supabaseFolder.parent_folder_id,
                workspaceId,
                collapsed: supabaseFolder.collapsed || false,
                boards: [],
                dashboards: [],
                docs: [],
                forms: [],
                apps: [],
                subFolders: [],
                createdAt: new Date(supabaseFolder.created_at),
                updatedAt: new Date(supabaseFolder.updated_at)
            };
            // Update in-memory workspace for immediate UI feedback
            if (folderData.parentId) {
                // Add to parent folder
                const parentFolder = this.findFolderInWorkspace(workspace, folderData.parentId);
                if (parentFolder) {
                    parentFolder.subFolders.push(folder);
                }
            }
            else {
                // Add to workspace root
                workspace.folders.push(folder);
            }
            workspace.updatedAt = new Date();
            console.log('✅ Folder created successfully:', folder);
            return folder;
        }
        catch (error) {
            console.error('❌ Error creating folder:', error);
            return null;
        }
    }
    /**
     * Create board in workspace - now integrated with COW board system
     */
    async createBoard(data) {
        const workspace = this.workspaces.get(data.workspaceId);
        if (!workspace)
            return null;
        try {
            // Create board using COW board service
            const mockUser = { id: data.ownerId || 'current-user', name: 'Current User', avatar: '' };
            const cowBoard = await boardService.createBoard({
                title: data.name,
                description: data.description,
                workspaceId: data.workspaceId, // Pass workspace ID to ensure board is created in correct workspace
                folderId: data.folderId, // Pass folder ID for organization
                type: 'project',
                settings: {
                    columnOrder: ['title', 'status', 'priority', 'assignee', 'dueDate'],
                    permissions: { public: true },
                    notifications: { enabled: true }
                }
            }, mockUser);
            // Convert COW board to workspace board format
            const workspaceBoard = {
                id: cowBoard.id,
                name: cowBoard.title,
                description: cowBoard.description,
                workspaceId: data.workspaceId,
                folderId: data.folderId,
                ownerId: data.ownerId || 'current-user',
                color: data.color || '#579bfc',
                starred: false,
                boardType: 'flexiboard',
                createdAt: cowBoard.createdAt,
                updatedAt: cowBoard.updatedAt
            };
            // Update workspace to track the board
            workspace.boards.push(workspaceBoard);
            // Add to folder if specified
            if (data.folderId) {
                const folder = this.findFolderInWorkspace(workspace, data.folderId);
                if (folder) {
                    folder.boards.push(workspaceBoard);
                }
            }
            workspace.updatedAt = new Date();
            return workspaceBoard;
        }
        catch (error) {
            console.error('Failed to create board:', error);
            return null;
        }
    }
    /**
     * Create dashboard in workspace
     */
    async createDashboard(data) {
        const workspace = this.workspaces.get(data.workspaceId);
        if (!workspace)
            return null;
        try {
            console.log('📊 workspace.service.ts: Creating dashboard via Supabase...', data);
            // Create dashboard in Supabase
            const supabaseDashboard = await supabaseWorkspaceService.createDashboard({
                workspaceId: data.workspaceId,
                folderId: data.folderId,
                name: data.name,
                description: data.description || '',
                createdBy: data.ownerId
            });
            if (!supabaseDashboard) {
                console.error('❌ Failed to create dashboard in Supabase');
                return null;
            }
            // Convert Supabase dashboard to workspace dashboard format
            const dashboard = {
                id: supabaseDashboard.id,
                name: supabaseDashboard.name,
                description: supabaseDashboard.description || '',
                workspaceId: data.workspaceId,
                folderId: supabaseDashboard.folder_id,
                ownerId: data.ownerId,
                starred: false,
                createdAt: new Date(supabaseDashboard.created_at),
                updatedAt: new Date(supabaseDashboard.updated_at)
            };
            // Update in-memory workspace for immediate UI feedback
            workspace.dashboards.push(dashboard);
            // Add to folder if specified
            if (data.folderId) {
                const folder = this.findFolderInWorkspace(workspace, data.folderId);
                if (folder) {
                    folder.dashboards.push(dashboard);
                }
            }
            workspace.updatedAt = new Date();
            console.log('✅ Dashboard created successfully:', dashboard);
            return dashboard;
        }
        catch (error) {
            console.error('❌ Error creating dashboard:', error);
            return null;
        }
    }
    /**
     * Create doc in workspace
     */
    async createDoc(data) {
        const workspace = this.workspaces.get(data.workspaceId);
        if (!workspace)
            return null;
        try {
            console.log('📄 workspace.service.ts: Creating doc via Supabase...', data);
            // Create doc in Supabase
            const supabaseDoc = await supabaseWorkspaceService.createDoc({
                workspaceId: data.workspaceId,
                folderId: data.folderId,
                name: data.name,
                content: data.content || '',
                createdBy: data.ownerId
            });
            if (!supabaseDoc) {
                console.error('❌ Failed to create doc in Supabase');
                return null;
            }
            // Convert Supabase doc to workspace doc format
            const doc = {
                id: supabaseDoc.id,
                name: supabaseDoc.name,
                description: data.description || '',
                workspaceId: data.workspaceId,
                folderId: supabaseDoc.folder_id,
                ownerId: data.ownerId,
                starred: false,
                createdAt: new Date(supabaseDoc.created_at),
                updatedAt: new Date(supabaseDoc.updated_at)
            };
            // Update in-memory workspace for immediate UI feedback
            workspace.docs.push(doc);
            // Add to folder if specified
            if (data.folderId) {
                const folder = this.findFolderInWorkspace(workspace, data.folderId);
                if (folder) {
                    folder.docs.push(doc);
                }
            }
            workspace.updatedAt = new Date();
            console.log('✅ Doc created successfully:', doc);
            return doc;
        }
        catch (error) {
            console.error('❌ Error creating doc:', error);
            return null;
        }
    }
    /**
     * Create form in workspace
     */
    async createForm(data) {
        const workspace = this.workspaces.get(data.workspaceId);
        if (!workspace)
            return null;
        try {
            console.log('📋 workspace.service.ts: Creating form via Supabase...', data);
            // Create form in Supabase
            const supabaseForm = await supabaseWorkspaceService.createForm({
                workspaceId: data.workspaceId,
                folderId: data.folderId,
                linkedBoardId: data.linkedBoardId,
                name: data.name,
                description: data.description || '',
                createdBy: data.ownerId
            });
            if (!supabaseForm) {
                console.error('❌ Failed to create form in Supabase');
                return null;
            }
            // Convert Supabase form to workspace form format
            const form = {
                id: supabaseForm.id,
                name: supabaseForm.name,
                description: supabaseForm.description || '',
                workspaceId: data.workspaceId,
                folderId: supabaseForm.folder_id,
                ownerId: data.ownerId,
                starred: false,
                linkedBoardId: supabaseForm.linked_board_id,
                submissionCount: supabaseForm.submission_count || 0,
                isActive: supabaseForm.is_active !== false,
                createdAt: new Date(supabaseForm.created_at),
                updatedAt: new Date(supabaseForm.updated_at)
            };
            // Update in-memory workspace for immediate UI feedback
            workspace.forms.push(form);
            // Add to folder if specified
            if (data.folderId) {
                const folder = this.findFolderInWorkspace(workspace, data.folderId);
                if (folder) {
                    folder.forms.push(form);
                }
            }
            workspace.updatedAt = new Date();
            console.log('✅ Form created successfully:', form);
            return form;
        }
        catch (error) {
            console.error('❌ Error creating form:', error);
            return null;
        }
    }
    async createApp(data) {
        const workspace = this.workspaces.get(data.workspaceId);
        if (!workspace)
            return null;
        try {
            console.log('🧩 workspace.service.ts: Creating app via Supabase...', data);
            // Create app in Supabase
            const supabaseApp = await supabaseWorkspaceService.createWorkspaceApp({
                workspaceId: data.workspaceId,
                folderId: data.folderId,
                appId: data.appId,
                name: data.name,
                description: data.description || '',
                createdBy: data.ownerId
            });
            if (!supabaseApp) {
                console.error('❌ Failed to create app in Supabase');
                return null;
            }
            // Convert Supabase app to workspace app format
            const app = {
                id: supabaseApp.id,
                appId: supabaseApp.app_id,
                name: supabaseApp.name,
                workspaceId: data.workspaceId,
                folderId: supabaseApp.folder_id,
                ownerId: data.ownerId,
                starred: false,
                settings: supabaseApp.config || {},
                isEnabled: supabaseApp.is_active !== false,
                addedAt: new Date(supabaseApp.created_at)
            };
            // Update in-memory workspace for immediate UI feedback
            workspace.apps.push(app);
            // Add to folder if specified
            if (data.folderId) {
                const folder = this.findFolderInWorkspace(workspace, data.folderId);
                if (folder) {
                    folder.apps.push(app);
                }
            }
            workspace.updatedAt = new Date();
            console.log('✅ App created successfully:', app);
            return app;
        }
        catch (error) {
            console.error('❌ Error creating app:', error);
            return null;
        }
    }
    /**
     * Update board - now integrated with COW board system
     */
    async updateBoard(boardId, updates) {
        try {
            // Update the COW board
            const boardUpdates = {};
            if (updates.name)
                boardUpdates.title = updates.name;
            if (updates.description)
                boardUpdates.description = updates.description;
            if (Object.keys(boardUpdates).length > 0) {
                await boardService.updateBoard(boardId, boardUpdates);
            }
            // Update workspace board tracking
            for (const workspace of this.workspaces.values()) {
                const board = workspace.boards.find(b => b.id === boardId);
                if (board) {
                    Object.assign(board, updates, { updatedAt: new Date() });
                    workspace.updatedAt = new Date();
                    // Update in folder if it exists
                    if (board.folderId) {
                        const folder = this.findFolderInWorkspace(workspace, board.folderId);
                        if (folder) {
                            const folderBoard = folder.boards.find(b => b.id === boardId);
                            if (folderBoard) {
                                Object.assign(folderBoard, updates, { updatedAt: new Date() });
                            }
                        }
                    }
                    return true;
                }
            }
            return false;
        }
        catch (error) {
            console.error('Failed to update board:', error);
            return false;
        }
    }
    /**
     * Update folder
     */
    updateFolder(folderId, updates) {
        for (const workspace of this.workspaces.values()) {
            const folder = this.findFolderInWorkspace(workspace, folderId);
            if (folder) {
                Object.assign(folder, updates, { updatedAt: new Date() });
                workspace.updatedAt = new Date();
                return true;
            }
        }
        return false;
    }
    /**
     * Delete board - now integrated with COW board system
     */
    async deleteBoard(workspaceId, boardId) {
        const workspace = this.workspaces.get(workspaceId);
        if (!workspace)
            return false;
        const boardIndex = workspace.boards.findIndex(b => b.id === boardId);
        if (boardIndex === -1)
            return false;
        try {
            // Delete from COW board system
            await boardService.deleteBoard(boardId);
            const board = workspace.boards[boardIndex];
            // Remove from workspace
            workspace.boards.splice(boardIndex, 1);
            // Remove from folder if it was in one
            if (board.folderId) {
                const folder = this.findFolderInWorkspace(workspace, board.folderId);
                if (folder) {
                    const folderBoardIndex = folder.boards.findIndex(b => b.id === boardId);
                    if (folderBoardIndex !== -1) {
                        folder.boards.splice(folderBoardIndex, 1);
                    }
                }
            }
            workspace.updatedAt = new Date();
            return true;
        }
        catch (error) {
            console.error('Failed to delete board:', error);
            return false;
        }
    }
    /**
     * Delete folder
     */
    deleteFolder(workspaceId, folderId) {
        const workspace = this.workspaces.get(workspaceId);
        if (!workspace)
            return false;
        // Find and remove the folder
        const removeFromArray = (folders) => {
            const index = folders.findIndex(f => f.id === folderId);
            if (index !== -1) {
                folders.splice(index, 1);
                return true;
            }
            // Check subfolders recursively
            for (const folder of folders) {
                if (removeFromArray(folder.subFolders)) {
                    return true;
                }
            }
            return false;
        };
        if (removeFromArray(workspace.folders)) {
            workspace.updatedAt = new Date();
            return true;
        }
        return false;
    }
    /**
     * Delete doc from workspace
     */
    async deleteDoc(workspaceId, docId) {
        try {
            console.log('📝 Deleting doc from Supabase:', docId);
            await supabaseWorkspaceService.deleteDoc(docId);
            console.log('✅ Doc deleted from Supabase');
            // Trigger workspace reload
            window.dispatchEvent(new Event('workspaces-changed'));
            return true;
        }
        catch (error) {
            console.error('❌ Failed to delete doc:', error);
            return false;
        }
    }
    /**
     * Delete dashboard from workspace
     */
    async deleteDashboard(workspaceId, dashboardId) {
        try {
            console.log('📊 Deleting dashboard from Supabase:', dashboardId);
            await supabaseWorkspaceService.deleteDashboard(dashboardId);
            console.log('✅ Dashboard deleted from Supabase');
            // Trigger workspace reload
            window.dispatchEvent(new Event('workspaces-changed'));
            return true;
        }
        catch (error) {
            console.error('❌ Failed to delete dashboard:', error);
            return false;
        }
    }
    /**
     * Delete form from workspace
     */
    async deleteForm(workspaceId, formId) {
        try {
            console.log('📋 Deleting form from Supabase:', formId);
            await supabaseWorkspaceService.deleteForm(formId);
            console.log('✅ Form deleted from Supabase');
            // Trigger workspace reload
            window.dispatchEvent(new Event('workspaces-changed'));
            return true;
        }
        catch (error) {
            console.error('❌ Failed to delete form:', error);
            return false;
        }
    }
    /**
     * Delete app from workspace
     */
    async deleteApp(workspaceId, appId) {
        try {
            console.log('🧩 Deleting app from Supabase:', appId);
            await supabaseWorkspaceService.deleteApp(appId);
            console.log('✅ App deleted from Supabase');
            // Trigger workspace reload
            window.dispatchEvent(new Event('workspaces-changed'));
            return true;
        }
        catch (error) {
            console.error('❌ Failed to delete app:', error);
            return false;
        }
    }
    /**
     * Move board to folder
     */
    moveBoardToFolder(workspaceId, boardId, folderId) {
        const workspace = this.workspaces.get(workspaceId);
        if (!workspace)
            return false;
        const board = workspace.boards.find(b => b.id === boardId);
        if (!board)
            return false;
        // Remove from old folder if it was in one
        if (board.folderId) {
            const oldFolder = this.findFolderInWorkspace(workspace, board.folderId);
            if (oldFolder) {
                oldFolder.boards = oldFolder.boards.filter(b => b.id !== boardId);
            }
        }
        // Add to new folder or workspace root
        board.folderId = folderId;
        if (folderId) {
            const newFolder = this.findFolderInWorkspace(workspace, folderId);
            if (newFolder) {
                newFolder.boards.push(board);
            }
        }
        workspace.updatedAt = new Date();
        return true;
    }
    /**
     * Toggle folder collapse state
     */
    toggleFolderCollapse(workspaceId, folderId) {
        const workspace = this.workspaces.get(workspaceId);
        if (!workspace)
            return false;
        const folder = this.findFolderInWorkspace(workspace, folderId);
        if (!folder)
            return false;
        folder.collapsed = !folder.collapsed;
        workspace.updatedAt = new Date();
        return true;
    }
    /**
     * Star/unstar board - now integrated with COW board system
     */
    async toggleBoardStar(workspaceId, boardId) {
        const workspace = this.workspaces.get(workspaceId);
        if (!workspace)
            return false;
        const board = workspace.boards.find(b => b.id === boardId);
        if (!board)
            return false;
        try {
            // Toggle star in COW board system
            await boardService.toggleBoardStar(boardId);
            // Update workspace board tracking
            board.starred = !board.starred;
            workspace.updatedAt = new Date();
            // Trigger workspace reload
            window.dispatchEvent(new Event('workspaces-changed'));
            return true;
        }
        catch (error) {
            console.error('Failed to toggle board star:', error);
            return false;
        }
    }
    /**
     * Star/unstar doc
     */
    async toggleDocStar(workspaceId, docId) {
        try {
            console.log('⭐ Toggling doc star in Supabase:', docId);
            const success = await supabaseWorkspaceService.toggleDocStar(docId);
            if (success) {
                console.log('✅ Doc star toggled in Supabase');
                // Trigger workspace reload
                window.dispatchEvent(new Event('workspaces-changed'));
            }
            return success;
        }
        catch (error) {
            console.error('❌ Failed to toggle doc star:', error);
            return false;
        }
    }
    /**
     * Star/unstar dashboard
     */
    async toggleDashboardStar(workspaceId, dashboardId) {
        try {
            console.log('⭐ Toggling dashboard star in Supabase:', dashboardId);
            const success = await supabaseWorkspaceService.toggleDashboardStar(dashboardId);
            if (success) {
                console.log('✅ Dashboard star toggled in Supabase');
                // Trigger workspace reload
                window.dispatchEvent(new Event('workspaces-changed'));
            }
            return success;
        }
        catch (error) {
            console.error('❌ Failed to toggle dashboard star:', error);
            return false;
        }
    }
    /**
     * Star/unstar form
     */
    async toggleFormStar(workspaceId, formId) {
        try {
            console.log('⭐ Toggling form star in Supabase:', formId);
            const success = await supabaseWorkspaceService.toggleFormStar(formId);
            if (success) {
                console.log('✅ Form star toggled in Supabase');
                // Trigger workspace reload
                window.dispatchEvent(new Event('workspaces-changed'));
            }
            return success;
        }
        catch (error) {
            console.error('❌ Failed to toggle form star:', error);
            return false;
        }
    }
    /**
     * Star/unstar app
     */
    async toggleAppStar(workspaceId, appId) {
        try {
            console.log('⭐ Toggling app star in Supabase:', appId);
            const success = await supabaseWorkspaceService.toggleAppStar(appId);
            if (success) {
                console.log('✅ App star toggled in Supabase');
                // Trigger workspace reload
                window.dispatchEvent(new Event('workspaces-changed'));
            }
            return success;
        }
        catch (error) {
            console.error('❌ Failed to toggle app star:', error);
            return false;
        }
    }
    /**
     * Get starred boards across all workspaces
     */
    getStarredBoards() {
        const starredBoards = [];
        for (const workspace of this.workspaces.values()) {
            const starred = workspace.boards.filter(b => b.starred);
            starredBoards.push(...starred);
        }
        return starredBoards.sort((a, b) => (b.lastViewed?.getTime() || 0) - (a.lastViewed?.getTime() || 0));
    }
    /**
     * Search boards across workspaces
     */
    searchBoards(query) {
        const results = [];
        const searchTerm = query.toLowerCase();
        for (const workspace of this.workspaces.values()) {
            const matches = workspace.boards.filter(board => board.name.toLowerCase().includes(searchTerm) ||
                board.description?.toLowerCase().includes(searchTerm));
            results.push(...matches);
        }
        return results;
    }
    findFolderInWorkspace(workspace, folderId) {
        // Search in root folders
        for (const folder of workspace.folders) {
            if (folder.id === folderId)
                return folder;
            // Search in subfolders recursively
            const subFolder = this.findFolderRecursive(folder, folderId);
            if (subFolder)
                return subFolder;
        }
        return null;
    }
    findFolderRecursive(folder, folderId) {
        for (const subFolder of folder.subFolders) {
            if (subFolder.id === folderId)
                return subFolder;
            const found = this.findFolderRecursive(subFolder, folderId);
            if (found)
                return found;
        }
        return null;
    }
    /**
     * Create sample boards asynchronously
     */
    async createSampleBoardsAsync(workspaceId, marketingFolderId, hrFolderId) {
        const sampleBoardsData = [
            {
                name: 'Marketing Campaigns',
                description: 'Track all marketing campaigns',
                workspaceId,
                folderId: marketingFolderId,
                ownerId: 'current-user',
                color: '#ff9f40',
                starred: true
            },
            {
                name: 'Lead Management',
                description: 'Track and manage leads',
                workspaceId,
                folderId: marketingFolderId,
                ownerId: 'current-user',
                color: '#579bfc'
            },
            {
                name: 'Hiring Pipeline',
                description: 'Track hiring process',
                workspaceId,
                folderId: hrFolderId,
                ownerId: 'current-user',
                color: '#00c875'
            },
            {
                name: 'General Tasks',
                description: 'General task management',
                workspaceId,
                ownerId: 'current-user',
                color: '#9d34da',
                starred: true
            }
        ];
        // Create boards asynchronously
        for (const boardData of sampleBoardsData) {
            try {
                const board = await this.createBoard(boardData);
                if (board && boardData.starred) {
                    // Update starred status after creation
                    await this.toggleBoardStar(workspaceId, board.id);
                }
            }
            catch (error) {
                console.warn('Failed to create sample board:', boardData.name, error);
            }
        }
    }
}
export const workspaceService = new WorkspaceService();
